import { component, prop, emit } from '../../../decorators';

import find from 'lodash/find';
import cond from 'lodash/cond';
import difference from 'lodash/difference';
import times from 'lodash/times';
import first from 'lodash/first';

import inRange from 'lodash/fp/inRange';

import SvgComponentMixin from '../../../mixins/svg.component.mixin';
import PeriodScaleMixin from '../../../mixins/period.scale.mixin';

import time from '../../../filters/time';

const SECONDARY_TICK_SIZE = 4;
const MAIN_TICK_SIZE = 10;

const MAX_TICK_MULTIPLIER = 1000;

const MAX_CANVAS_WIDTH = 16384;

const multiplierForScale = cond([
   [ inRange(0,  3),    () => 50 * MAX_TICK_MULTIPLIER ],
   [ inRange(3,  5),    () => 25 * MAX_TICK_MULTIPLIER ],
   [ inRange(5,  10),   () => 20 * MAX_TICK_MULTIPLIER ],
   [ inRange(10, 30),   () => 10 * MAX_TICK_MULTIPLIER ],
   [ inRange(30, 40),   () => 5  * MAX_TICK_MULTIPLIER ],
   [ () => true,        () => 2  * MAX_TICK_MULTIPLIER ]
]);

const secondsMultiplierForScale = cond([
   [ inRange(0,  3),    () => 5 * MAX_TICK_MULTIPLIER ],
   [ inRange(3,  5),    () => 2 * MAX_TICK_MULTIPLIER ],
   [ () => true,        () => 1 * MAX_TICK_MULTIPLIER ]
]);

const _increment = i => ++i;
const _decrement = i => --i;

@component({
   mixins: [ SvgComponentMixin, PeriodScaleMixin ]
})
export default class Timeline {
   @prop({ type: Number, required: true }) scale

   get ticksCount() {
      return this.maxExtent - this.minExtent;
   }

   get secondsTicksMultiplier() {
      return secondsMultiplierForScale(this.scale);
   }

   get mainTicksMultiplier() {
      return multiplierForScale(this.scale);
   }

   get countOfCanvases() {
      return Math.ceil(this.width / MAX_CANVAS_WIDTH);
   }

   get canvases() {
      const { totalWidth } = this;
      return times(this.countOfCanvases, idx => {
         const from = idx * MAX_CANVAS_WIDTH;
         const to = Math.min(totalWidth, _increment(idx) * MAX_CANVAS_WIDTH);
         const width = to - from;
         return { idx, from, to, width };
      });
   }

   @emit('change-current-time') changeCurrentTime

   ticks(multiplier) {
      const count = Math.floor( this.ticksCount / multiplier );
      return times(count, idx => this.minExtent + idx * multiplier);
   }

   paint() {
      const { totalHeight, marginBottom } = this;

      const bottom = _decrement(totalHeight - marginBottom);

      const mainTicks = this.ticks(this.mainTicksMultiplier);
      const secsTicks = difference(this.ticks(this.secondsTicksMultiplier), mainTicks);

      this._paintSecsTicks(secsTicks, bottom);
      this._paintMainTicks(mainTicks, bottom);
   }

   _paintSecsTicks(secsTicks, bottom) {
      const _prepareContext = ctx => {
         ctx.beginPath();
         ctx.strokeStyle = '#666';
      };

      const _paintTick = (ctx, posX) => {
         const textBottom = bottom - SECONDARY_TICK_SIZE;
         ctx.moveTo(posX, textBottom);
         ctx.lineTo(posX, bottom);
         ctx.stroke(); // facking safari
      };

      this._canvasIterator(secsTicks, _prepareContext, _paintTick);
   }

   _paintMainTicks(mainTicks, bottom) {
      const { marginLeft, marginRight, totalWidth } = this;

      const _prepareContext = ctx => {
         ctx.font = '9px Montserrat';
         ctx.textBaseline = 'bottom';
         ctx.textAlign = 'center';
         ctx.strokeStyle = '#333';
         ctx.beginPath(); // facking safari
         ctx.moveTo(marginLeft, bottom);
         ctx.lineTo(totalWidth - marginRight, bottom);
         ctx.stroke(); // facking safari
      };

      const _paintTick = (ctx, posX, d) => {
         const textBottom = bottom - MAIN_TICK_SIZE;
         const txt = _timeFormatter(d);
         ctx.beginPath(); // facking safari
         ctx.moveTo(posX, textBottom);
         ctx.lineTo(posX, bottom);
         ctx.fillText(txt, posX, textBottom);
         ctx.stroke(); // facking safari
      };

      this._canvasIterator(mainTicks, _prepareContext, _paintTick, true);
   }

   _canvasIterator(ticks, _prepareContext, _paintTick, _renderNext) {
      let canvas = first(this.canvases);
      let ctx = this._ctx4canvas(canvas);
      _prepareContext(ctx);

      ticks.forEach(d => {
         const _x = this.x(d);
         if ( canvas.from > _x || canvas.to < _x ) {
            if (_renderNext) {
               _paintTick(ctx, _x - canvas.from, d);
            }
            // ctx.stroke();

            canvas = this._findCanvas4point(_x);
            ctx = this._ctx4canvas(canvas);
            _prepareContext(ctx);
         }
         _paintTick(ctx, _x - canvas.from, d);
      });

      // ctx.stroke();
   }

   _ctx4canvas({ idx }) {
      const elements = this.$el.querySelectorAll('canvas');
      return elements[idx].getContext('2d');
   }

   _findCanvas4point(d) {
      return find( this.canvases, ({ from, to }) => inRange(from, to, d) );
   }

   onClick({ offsetX, target }) {
      const left = parseInt(target.style.left, 10);
      const time = Math.ceil((offsetX + left) * this.ticksCount / this.width) + this.minExtent;
      this.changeCurrentTime(time);
   }

   x(tc) {
      const { minExtent, maxExtent, width } = this;
      return width * ( tc - minExtent ) / ( maxExtent - minExtent );
   }

   afterBrushed() {
      this.paint(); // TODO
   }

   mounted() {
      this.paint();
   }
}

function _timeFormatter(d) {
   return time(d, true);
}
